// ─────────────────────────────────────────────────────────────────────────────
// demoApi.js — fake backend for demo mode.
//
// One handler per endpoint the four demo pages call, each returning the exact
// response shape its real route returns. Where the real route derives a number
// in SQL, the handler derives it here from the same rows it is about to serve —
// so the KPI headers always agree with the tables under them. A header claiming
// 30 jobs above a list of 24 is the fastest way to lose a room.
//
// Response shapes were read off the backend, not guessed:
//   /schedule/history            schedulingRoutes.js
//   /schedule/stats              schedulingRoutes.js
//   /schedule/teams              schedulingRoutes.js
//   /schedule/calendar/:m/:y     schedulingRoutes.js
//   /attendance/{records,filters,summary,coverage}
//                                attendanceRoutes.js
//   /fo-schedules/:id/detail     ciFOSchedulesRoutes.js
//   /ci-performance/{mandates,billing-status}
//                                ciPerformanceRoutes.js
//   /commercial-maintenance/{templates,calendar}
//                                ciMaintenanceRoutes.js
//   /commercial-sites/:clientId  commericalSites.js
//
// READ-ONLY. Every mutating route resolves with a success-shaped body and writes
// nothing. That is deliberate: several pages call alert() on a failed save
// (MandateConfig.js does), and a browser alert box in the middle of a
// presentation is worse than a save that quietly does nothing. The DEMO badge
// raises a toast instead so the behaviour is still honest on screen.
// ─────────────────────────────────────────────────────────────────────────────

import { getData, withResolvedPhotos } from './demoStore';

// Photo slots are stored raw (a /uploads/ path, or an idb: token) and resolved
// on the way out — see withResolvedPhotos. Every handler that returns a record
// with photos on it must pass through these, or the pages get a raw token they
// cannot render.
const outJob = (j) => withResolvedPhotos(j, 'job');
const outAttendance = (a) => withResolvedPhotos(a, 'attendance');

const UNASSIGNED = 'Unassigned';

// ─── Small helpers ───────────────────────────────────────────────────────────

const ok = (body) => ({ status: 200, data: body });
const round = (v, dp) => (v == null ? null : Number(Number(v).toFixed(dp)));
const uniq = (list) => [...new Set(list.filter((x) => x != null && x !== ''))];

/** Today in Asia/Karachi. The coverage endpoint defaults to it. */
function todayPKT() {
  return new Date(Date.now() + 5 * 3600 * 1000).toISOString().slice(0, 10);
}

/** Hours between two instants, or null if the shift is still open. */
function spanHours(a, b) {
  if (!a || !b) return null;
  return (new Date(b).getTime() - new Date(a).getTime()) / 3600000;
}

/** Shared date/client/city filter used by every attendance endpoint. */
function filterAttendance(rows, { from, to, client_id, city, fo_name, job_id, status }) {
  return rows.filter((r) => {
    if (from && r.attendance_date < from) return false;
    if (to && r.attendance_date > to) return false;
    if (client_id && String(r.client_id) !== String(client_id)) return false;
    if (city && String(r.city || '').toLowerCase() !== String(city).toLowerCase()) return false;
    if (fo_name && !String(r.fo_name).toLowerCase().includes(String(fo_name).toLowerCase())) return false;
    if (job_id && String(r.job_id) !== String(job_id)) return false;
    if (status && r.status !== status) return false;
    return true;
  });
}

// ─── /schedule/history ───────────────────────────────────────────────────────

function scheduleHistory(params) {
  const { jobs } = getData();
  const { status, dateRange, search, team_id } = params;
  const today = todayPKT();

  let out = jobs;

  if (status && status !== 'all') out = out.filter((j) => j.status === status);
  if (team_id) out = out.filter((j) => String(j.team_id) === String(team_id));

  if (dateRange && dateRange !== 'all') {
    const days = { today: 0, week: 7, month: 30, year: 365 }[dateRange];
    if (days === 0) out = out.filter((j) => j.scheduled_date === today);
    else if (days) {
      const cutoff = new Date(Date.parse(`${today}T12:00:00Z`) - days * 86400000)
        .toISOString().slice(0, 10);
      out = out.filter((j) => j.scheduled_date >= cutoff);
    }
  }

  if (search) {
    const q = String(search).toLowerCase();
    out = out.filter((j) =>
      String(j.site_name || '').toLowerCase().includes(q) ||
      String(j.client_name || '').toLowerCase().includes(q) ||
      String(j.address || '').toLowerCase().includes(q));
  }

  return ok({ success: true, jobs: out.map(outJob) });
}

function scheduleStats() {
  const { jobs } = getData();
  const count = (fn) => jobs.filter(fn).length;
  return ok({
    total: jobs.length,
    completed: count((j) => j.status === 'completed'),
    inProgress: count((j) => ['started', 'before_photos', 'after_photos'].includes(j.status)),
    scheduled: count((j) => j.status === 'scheduled'),
    rescheduled: count((j) => j.status === 'rescheduled'),
  });
}

// ─── /schedule/teams ─────────────────────────────────────────────────────────

function scheduleTeams() {
  const { teams, jobs } = getData();
  const today = todayPKT();
  return ok({
    success: true,
    count: teams.length,
    teams: teams.map((t) => {
      const mine = jobs.filter((j) => j.team_id === t.team_id);
      return {
        id: t.team_id,
        team_lead_name: t.team_lead_name,
        team_lead_name_from_users: t.team_lead_name,
        team_lead_email: null,
        team_lead_username: null,
        phone: t.phone,
        is_active: true,
        total_assignments: mine.length,
        upcoming_assignments: mine.filter(
          (j) => j.scheduled_date >= today &&
            ['scheduled', 'in_progress', 'rescheduled'].includes(j.status),
        ).length,
      };
    }),
  });
}

// ─── /schedule/calendar/:month/:year ─────────────────────────────────────────

function scheduleCalendar(month, year) {
  const { clients, jobs, teams } = getData();
  const m = Number(month);
  const y = Number(year);

  const inMonth = (d) => {
    if (!d) return false;
    const [jy, jm] = d.split('-').map(Number);
    return jy === y && jm === m;
  };

  return ok({
    success: true,
    month: m,
    year: y,
    totalClients: clients.length,
    clients: clients.map((c) => {
      const mine = jobs.filter((j) => j.client_id === c.client_id);
      const team = teams.find((t) => t.team_id === c.team_id);
      return {
        client_id: c.client_id,
        client_name: c.client_name,
        contractual_services_count: null,
        contract_cycles: null,
        jobs_per_cycle: null,
        completed_jobs: mine.filter((j) => j.status === 'completed').length,
        total_sites: String(c.sites.length),
        schedules: mine.filter((j) => inMonth(j.scheduled_date)).map((j) => ({
          schedule_id: j.schedule_id,
          client_id: j.client_id,
          site_id: j.site_id,
          team_id: j.team_id,
          scheduled_date: j.scheduled_date,
          original_date: j.original_date,
          service_number: j.service_number,
          status: j.status,
          notes: j.notes,
          shift: j.shift,
          previous_shift: j.previous_shift,
          reschedule_reason: j.reschedule_reason,
          team_lead_name: team ? team.team_lead_name : j.team_lead_name,
          client_name: j.client_name,
          site_name: j.site_name,
        })),
      };
    }),
  });
}

// ─── /commercial-sites/:clientId ─────────────────────────────────────────────

function commercialSites(clientId) {
  const { clients } = getData();
  const c = clients.find((x) => String(x.client_id) === String(clientId));
  if (!c) return ok([]);
  // Bare array, exactly as the real route returns it.
  return ok(c.sites.map((s) => ({
    id: s.site_id,
    client_id: c.client_id,
    site_name: s.site_name,
    address: c.address,
    city: c.city,
    state: c.state,
    zip_code: '',
    system_type: s.system_type,
    latitude: c.latitude,
    longitude: c.longitude,
    images: null,
    is_active: true,
  })));
}

// ─── /commercial-maintenance/* ───────────────────────────────────────────────

/** Real rows from maintenance_task_templates. */
const MAINTENANCE_TEMPLATES = [
  { id: 1, task_number: 2, task_name: 'Site Housekeeping & Organization', category: 'non_technical', check_points: 'Tools & equipment arranged, vehicles clean, PPE labeled', sort_order: 2, is_active: true },
  { id: 2, task_number: 3, task_name: 'Cleaning of Plant Premises', category: 'non_technical', check_points: 'Walkways/drains cleaned, PPE used', sort_order: 3, is_active: true },
  { id: 3, task_number: 4, task_name: 'Cleaning of System Components', category: 'non_technical', check_points: 'Inverters, cable trays, foundations cleaned', sort_order: 4, is_active: true },
  { id: 4, task_number: 5, task_name: 'Vegetation Management', category: 'non_technical', check_points: 'Grass cleared, no shading, drainage open', sort_order: 5, is_active: true },
  { id: 5, task_number: 6, task_name: 'Logistics & Movement', category: 'non_technical', check_points: 'Proper loading/unloading, water tanks arranged', sort_order: 6, is_active: true },
  { id: 6, task_number: 7, task_name: 'Documentation & Reporting', category: 'non_technical', check_points: 'Logs updated, client signature collected', sort_order: 7, is_active: true },
  { id: 7, task_number: 8, task_name: 'HSSE Compliance & Monitoring', category: 'non_technical', check_points: 'PPE used, waste disposed properly, fire equipment checked', sort_order: 8, is_active: true },
  { id: 8, task_number: 9, task_name: 'Inspection & Test Plan Support', category: 'non_technical', check_points: 'Boundaries secure, signage & first-aid available', sort_order: 9, is_active: true },
];

const maintenanceTemplates = () =>
  ok({ success: true, templates: MAINTENANCE_TEMPLATES, total: MAINTENANCE_TEMPLATES.length });

// The demo covers the cleaning calendar, not the separate maintenance one. An
// empty map is a valid response and the page already handles it.
const maintenanceCalendar = () => ok({ success: true, schedules: {} });

// ─── /attendance/records ─────────────────────────────────────────────────────

function attendanceRecords(params) {
  const { attendance } = getData();
  const rows = filterAttendance(attendance, params).slice(0, 2000);
  return ok({ success: true, count: rows.length, records: rows.map(outAttendance) });
}

// ─── /attendance/filters ─────────────────────────────────────────────────────

function attendanceFilters(params) {
  const { attendance } = getData();
  const rows = filterAttendance(attendance, { from: params.from, to: params.to });

  const seen = new Map();
  rows.forEach((r) => {
    if (r.client_id != null && !seen.has(r.client_id)) {
      seen.set(r.client_id, { client_id: r.client_id, client_name: r.client_name });
    }
  });

  return ok({
    success: true,
    clients: [...seen.values()].sort((a, b) => a.client_name.localeCompare(b.client_name)),
    cities: uniq(rows.map((r) => r.city)).sort(),
  });
}

// ─── /attendance/summary ─────────────────────────────────────────────────────

const GROUPERS = {
  person: { key: (r) => r.fo_name, id: () => null },
  client: { key: (r) => r.client_name || UNASSIGNED, id: (r) => r.client_id },
  city: { key: (r) => r.city || UNASSIGNED, id: () => null },
};

function attendanceSummary(params) {
  const { attendance } = getData();
  const groupBy = String(params.group_by || 'person').toLowerCase();
  const grouper = GROUPERS[groupBy];
  if (!grouper) {
    return {
      status: 400,
      data: {
        success: false,
        message: `Invalid group_by. Expected one of: ${Object.keys(GROUPERS).join(', ')}.`,
      },
    };
  }

  const rows = filterAttendance(attendance, params);
  const closed = rows.filter((r) => r.clock_out_at);
  const hoursOf = (r) => spanHours(r.clock_in_at, r.clock_out_at) || 0;
  const totalHours = closed.reduce((n, r) => n + hoursOf(r), 0);

  const kpis = {
    total_checkins: rows.length,
    total_jobs: uniq(rows.map((r) => r.job_id)).length,
    people: uniq(rows.map((r) => r.fo_name)).length,
    missing_clockout: rows.filter((r) => !r.clock_out_at).length,
    unlinked_checkins: rows.filter((r) => r.job_id == null).length,
    active_days: uniq(rows.map((r) => r.attendance_date)).length,
    avg_hours: closed.length ? round(totalHours / closed.length, 2) : null,
    total_hours: closed.length ? round(totalHours, 1) : null,
  };

  const buckets = new Map();
  rows.forEach((r) => {
    const label = grouper.key(r);
    if (!buckets.has(label)) {
      buckets.set(label, { label, group_id: grouper.id(r), rows: [] });
    }
    buckets.get(label).rows.push(r);
  });

  const grouped = [...buckets.values()].map((b) => {
    const done = b.rows.filter((r) => r.clock_out_at);
    const hrs = done.reduce((n, r) => n + hoursOf(r), 0);
    return {
      group_id: b.group_id,
      label: b.label,
      jobs: uniq(b.rows.map((r) => r.job_id)).length,
      checkins: b.rows.length,
      people: uniq(b.rows.map((r) => r.fo_name)).length,
      days: uniq(b.rows.map((r) => r.attendance_date)).length,
      missing_clockout: b.rows.filter((r) => !r.clock_out_at).length,
      total_hours: done.length ? round(hrs, 1) : null,
      avg_hours: done.length ? round(hrs / done.length, 2) : null,
      last_seen: b.rows.reduce((m, r) => (r.attendance_date > m ? r.attendance_date : m), ''),
    };
  }).sort((a, b) => b.checkins - a.checkins);

  const byDate = new Map();
  rows.forEach((r) => {
    if (!byDate.has(r.attendance_date)) byDate.set(r.attendance_date, []);
    byDate.get(r.attendance_date).push(r);
  });
  const daily = [...byDate.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([date, rs]) => ({
      date,
      checkins: rs.length,
      people: uniq(rs.map((r) => r.fo_name)).length,
      jobs: uniq(rs.map((r) => r.job_id)).length,
    }));

  return ok({ success: true, kpis, group_by: groupBy, rows: grouped, daily });
}

// ─── /attendance/coverage ────────────────────────────────────────────────────

/**
 * "Who did not turn up", client by client.
 *
 * There is no FO->client assignment table in the real schema, so the backend
 * infers each client's roster from history: everyone who has ever clocked in for
 * that client. This mirrors that exactly, including the three-way split —
 * present here / present elsewhere / absent everywhere — because without the
 * "elsewhere" bucket someone working another site today reads as a no-show.
 */
function attendanceCoverage(params) {
  const { attendance, jobs, clients } = getData();
  const from = params.from || todayPKT();
  const to = params.to || from;

  const inRange = attendance.filter((r) => r.attendance_date >= from && r.attendance_date <= to);

  // Roster from all history, keyed on fo_name — the only reliable identity.
  const roster = new Map(); // client_id -> Set(fo_name)
  attendance.forEach((r) => {
    if (r.client_id == null) return;
    if (!roster.has(r.client_id)) roster.set(r.client_id, new Set());
    roster.get(r.client_id).add(r.fo_name);
  });

  const presentAt = new Map(); // `${client_id}|${fo}` -> rows
  const punchedAnywhere = new Map(); // fo -> Set(client_name)
  inRange.forEach((r) => {
    const k = `${r.client_id}|${r.fo_name}`;
    if (!presentAt.has(k)) presentAt.set(k, []);
    presentAt.get(k).push(r);
    if (!punchedAnywhere.has(r.fo_name)) punchedAnywhere.set(r.fo_name, new Set());
    punchedAnywhere.get(r.fo_name).add(r.client_name);
  });

  const lastSeen = new Map(); // fo -> date
  attendance.forEach((r) => {
    const cur = lastSeen.get(r.fo_name);
    if (!cur || r.attendance_date > cur) lastSeen.set(r.fo_name, r.attendance_date);
  });

  let rows = [...roster.entries()].map(([clientId, names]) => {
    const client = clients.find((c) => c.client_id === clientId);
    const people = [...names].map((fo) => {
      const here = presentAt.get(`${clientId}|${fo}`) || [];
      const elsewhere = punchedAnywhere.get(fo);
      let state = 'absent';
      if (here.length) state = 'present';
      else if (elsewhere && elsewhere.size) state = 'elsewhere';
      return {
        fo_name: fo,
        state,
        last_seen: lastSeen.get(fo) || null,
        shifts: here.length,
        at: state === 'elsewhere' ? [...elsewhere].join(', ') : null,
      };
    }).sort((a, b) => (a.state === b.state ? a.fo_name.localeCompare(b.fo_name) : a.state.localeCompare(b.state)));

    const count = (s) => people.filter((p) => p.state === s).length;
    return {
      client_id: clientId,
      client_name: client ? client.client_name : UNASSIGNED,
      city: client ? client.city : UNASSIGNED,
      scheduled_jobs: jobs.filter(
        (j) => j.client_id === clientId && j.scheduled_date >= from && j.scheduled_date <= to,
      ).length,
      expected: people.length,
      present: count('present'),
      elsewhere: count('elsewhere'),
      absent: count('absent'),
      people,
    };
  });

  if (params.client_id) rows = rows.filter((r) => String(r.client_id) === String(params.client_id));
  if (params.city) {
    rows = rows.filter((r) => String(r.city).toLowerCase() === String(params.city).toLowerCase());
  }
  rows.sort((a, b) => (b.absent - a.absent) || a.client_name.localeCompare(b.client_name));

  // Headline figures count each PERSON once company-wide. Per-client columns
  // cannot be summed for this — somebody on three rosters would be counted
  // three times.
  const workforce = uniq(attendance.map((r) => r.fo_name));
  const clockedIn = workforce.filter((fo) => punchedAnywhere.has(fo)).length;

  return ok({
    success: true,
    range: { from, to },
    kpis: {
      clients: rows.length,
      workforce: workforce.length,
      clocked_in: clockedIn,
      no_clock_in: workforce.length - clockedIn,
      coverage_pct: workforce.length ? Math.round((clockedIn / workforce.length) * 100) : 0,
    },
    rows,
  });
}

// ─── /fo-schedules/:id/detail ────────────────────────────────────────────────

function jobDetail(scheduleId) {
  const { jobs } = getData();
  const job = jobs.find((j) => String(j.schedule_id) === String(scheduleId));
  if (!job) {
    return { status: 404, data: { success: false, message: 'Job not found' } };
  }
  return ok({ success: true, job: outJob(job) });
}

// ─── /ci-performance/* ───────────────────────────────────────────────────────

function ciMandates() {
  const { mandates } = getData();
  return ok({ success: true, count: mandates.length, data: mandates });
}

/**
 * All-time man-hours cycle/billing status.
 *
 * Mirrors runCompute() in ciPerformanceRoutes.js, and the two subtleties there
 * matter for the numbers on screen:
 *   - DELIVERED is headcount x hours_per_member_per_day for each date worked,
 *     NOT actual clocked hours. Actual is reported separately.
 *   - The cycle counter resets cumulative to zero every time a full mandate is
 *     banked, so progress_pct is progress through the CURRENT cycle. A client
 *     that just completed one legitimately shows a low percentage next to a
 *     billing-ready flag.
 * Only clients with a mandate row appear, same as the real route.
 */
function ciBillingStatus(params) {
  const { attendance, mandates } = getData();
  const map = {};

  mandates.filter((m) => m.has_mandate).forEach((m) => {
    if (params.client_id && String(params.client_id) !== String(m.client_id)) return;

    const total = m.man_hours_supervisor + m.man_hours_fo + m.man_hours_engineer + m.man_hours_technician;
    const hpd = m.hours_per_member_per_day || 8;
    const mine = attendance.filter((r) => r.client_id === m.client_id);

    const byDate = new Map();
    mine.forEach((r) => {
      if (!byDate.has(r.attendance_date)) byDate.set(r.attendance_date, new Map());
      const day = byDate.get(r.attendance_date);
      const span = spanHours(r.clock_in_at, r.clock_out_at) || 0;
      // Dedupe duplicate same-day rows for one person: keep the longest span.
      const prev = day.get(r.fo_name);
      day.set(r.fo_name, {
        actualMax: prev ? Math.max(prev.actualMax, span) : span,
        complete: Boolean(r.clock_in_at && r.clock_out_at) && (prev ? prev.complete : true),
      });
    });

    let delivered = 0;
    let actual = 0;
    let cumulative = 0;
    let cycles = 0;
    let incomplete = 0;

    [...byDate.keys()].sort().forEach((d) => {
      const members = byDate.get(d);
      const dayHours = members.size * hpd;
      delivered += dayHours;
      cumulative += dayHours;
      members.forEach((info) => {
        actual += info.actualMax;
        if (!info.complete) incomplete += 1;
      });
      if (total > 0 && cumulative >= total) { cycles += 1; cumulative = 0; }
    });

    map[m.client_id] = {
      mandate_total: total,
      delivered_total: delivered,
      actual_total: round(actual, 1),
      cycles_completed: cycles,
      contract_cycles: null,
      billing_ready: cycles >= 1,
      progress_pct: total > 0 ? Math.min(100, Math.round((cumulative / total) * 100)) : 0,
      incomplete_punches: incomplete,
    };
  });

  return ok({ success: true, count: Object.keys(map).length, data: map });
}

// ─── Route table ─────────────────────────────────────────────────────────────

const ROUTES = [
  { m: 'GET', re: /^\/schedule\/history$/, fn: (_, p) => scheduleHistory(p) },
  { m: 'GET', re: /^\/schedule\/stats$/, fn: () => scheduleStats() },
  { m: 'GET', re: /^\/schedule\/teams$/, fn: () => scheduleTeams() },
  { m: 'GET', re: /^\/schedule\/calendar\/(\d+)\/(\d+)$/, fn: (mt) => scheduleCalendar(mt[1], mt[2]) },
  { m: 'GET', re: /^\/commercial-maintenance\/templates$/, fn: () => maintenanceTemplates() },
  { m: 'GET', re: /^\/commercial-maintenance\/calendar\/(\d+)\/(\d+)$/, fn: () => maintenanceCalendar() },
  { m: 'GET', re: /^\/commercial-sites\/(\d+)$/, fn: (mt) => commercialSites(mt[1]) },
  { m: 'GET', re: /^\/attendance\/records$/, fn: (_, p) => attendanceRecords(p) },
  { m: 'GET', re: /^\/attendance\/filters$/, fn: (_, p) => attendanceFilters(p) },
  { m: 'GET', re: /^\/attendance\/summary$/, fn: (_, p) => attendanceSummary(p) },
  { m: 'GET', re: /^\/attendance\/coverage$/, fn: (_, p) => attendanceCoverage(p) },
  { m: 'GET', re: /^\/fo-schedules\/(\d+)\/detail$/, fn: (mt) => jobDetail(mt[1]) },
  { m: 'GET', re: /^\/ci-performance\/mandates$/, fn: () => ciMandates() },
  { m: 'GET', re: /^\/ci-performance\/billing-status$/, fn: (_, p) => ciBillingStatus(p) },
];

/**
 * Mutating routes the demo pages can reach. Matched so they resolve cleanly
 * instead of escaping to the network — a real write from a demo session would
 * edit production data.
 */
const WRITE_ROUTES = [
  /^\/schedule\/edit\//,
  /^\/schedule\/bulk-/,
  /^\/schedule\/\d+$/,
  /^\/schedule$/,
  /^\/ci-performance\/mandates\/\d+$/,
  /^\/ci-petty-cash\/request$/,
  /^\/commercial-maintenance\/schedule-tasks$/,
  /^\/fo-schedules\//,
];

/** Strip origin, /api prefix and query string; return the bare path + params. */
export function normalizeUrl(rawUrl, baseUrl) {
  let u = String(rawUrl || '');
  if (baseUrl && !/^https?:/i.test(u) && !u.startsWith('/')) {
    u = `${String(baseUrl).replace(/\/+$/, '')}/${u}`;
  }
  const qIdx = u.indexOf('?');
  const query = qIdx >= 0 ? u.slice(qIdx + 1) : '';
  if (qIdx >= 0) u = u.slice(0, qIdx);
  u = u.replace(/^https?:\/\/[^/]+/i, '');
  u = u.replace(/^\/api(?=\/|$)/, '');
  if (!u.startsWith('/')) u = `/${u}`;
  u = u.replace(/\/+$/, '') || '/';

  const params = {};
  if (query) {
    new URLSearchParams(query).forEach((v, k) => { params[k] = v; });
  }
  return { path: u, params };
}

/**
 * Resolve a request against the demo dataset.
 * Returns null when nothing matches, which tells the adapter to let the request
 * go to the real network.
 */
export function handleDemoRequest({ method, url, baseUrl, params: extraParams }) {
  const verb = String(method || 'get').toUpperCase();
  const { path, params } = normalizeUrl(url, baseUrl);
  const merged = { ...params, ...(extraParams || {}) };

  if (verb === 'GET') {
    for (let i = 0; i < ROUTES.length; i += 1) {
      const route = ROUTES[i];
      if (route.m !== 'GET') continue;
      const mt = path.match(route.re);
      if (mt) return { ...route.fn(mt, merged), demoPath: path };
    }
    return null;
  }

  // Non-GET: swallow anything aimed at a route the demo pages touch.
  const isKnownWrite = WRITE_ROUTES.some((re) => re.test(path)) ||
    ROUTES.some((r) => r.re.test(path));
  if (isKnownWrite) {
    return {
      status: 200,
      demoPath: path,
      demoReadOnly: true,
      data: { success: true, demo: true, message: 'Read-only demo — change not saved.' },
    };
  }

  return null;
}

export default handleDemoRequest;
